import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useSession } from "@/context/Session";
import { errMsg } from "@/lib/api";
import { brl, cnpjMask, fdate, label } from "@/lib/format";
import { Badge, Empty, Loading, PageHeader } from "./ui";
import { DocField, DocLink } from "./Docs";

export const fmt = (type, v, docs) => {
  if (type === "money") return brl(v);
  if (type === "date" || type === "month") return fdate(v);
  if (type === "status") return <Badge s={v} />;
  if (type === "doc") return <DocLink id={v} docs={docs} />;
  if (type === "label") return label(v);
  return v || "—";
};

function Field({ f, value, set }) {
  const common = { "data-testid": `field-${f.name}`, className: "field", value: value ?? "", onChange: (e) => set(f.mask === "cnpj" ? cnpjMask(e.target.value) : e.target.value) };
  if (f.mask === "cnpj") return <input {...common} inputMode="numeric" maxLength={18} placeholder="00.000.000/0000-00" />;
  if (f.type === "select") return <select {...common}>{f.options.map((o) => <option key={o} value={o}>{label(o)}</option>)}</select>;
  if (f.type === "textarea") return <textarea {...common} className="field h-24 py-2" />;
  if (f.type === "doc") return <DocField name={f.name} value={value} onChange={set} category={f.category} />;
  if (f.type === "checkbox") return <input type="checkbox" data-testid={`field-${f.name}`} checked={!!value} onChange={(e) => set(e.target.checked)} className="h-5 w-5" />;
  return <input {...common} type={f.type === "money" ? "number" : f.type || "text"} step={f.type === "money" ? "0.01" : undefined} />;
}

export function CrudForm({ fields, initial, onSubmit, submitLabel = "Salvar", lookup }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [looking, setLooking] = useState(false);
  const seq = useRef(0);
  const setField = async (f, v) => {
    setForm((p) => ({ ...p, [f.name]: v }));
    const digits = String(v || "").replace(/\D/g, "");
    if (!f.lookup || !lookup || digits.length !== 14) return;
    const my = ++seq.current;
    setLooking(true);
    const found = await lookup(digits);
    if (my !== seq.current) return;
    setLooking(false);
    if (found) setForm((p) => ({ ...p, ...found, [f.name]: v }));
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const data = { ...form };
    fields.forEach((f) => { if (f.type === "money" || f.type === "number") data[f.name] = Number(data[f.name] || 0); });
    try { await onSubmit(data); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
      {fields.map((f) => (
        <label key={f.name} className={`block text-sm font-semibold text-[#12343B] ${f.wide || f.type === "textarea" || f.type === "doc" ? "sm:col-span-2" : ""}`}>
          {f.label}{f.required && " *"}
          <div className="mt-1.5"><Field f={f} value={form[f.name]} set={(v) => setField(f, v)} /></div>
          {f.lookup && lookup && <span className="mt-1 block text-xs font-normal text-[#587178]" data-testid="cnpj-lookup-status">{looking ? "Buscando dados na Receita Federal..." : "Digite o CNPJ para preencher os dados automaticamente."}</span>}
          {f.help && <span className="mt-1 block text-xs font-normal text-[#587178]">{f.help}</span>}
        </label>))}
      <div className="sm:col-span-2 flex justify-end"><button className="btn-primary w-full sm:w-auto" disabled={busy} data-testid="form-submit">{busy ? "Salvando..." : submitLabel}</button></div>
    </form>
  );
}

export default function CrudPage({ title, subtitle, resource, path, fields, columns, defaults = {}, filter, notice, headerless, testid, rowActions }) {
  const { api, can } = useSession();
  const [items, setItems] = useState(null);
  const [docs, setDocs] = useState([]);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const writable = can(resource, "w");
  const load = () => api.get(path).then((r) => setItems(r.data)).catch((e) => { setItems([]); toast.error(errMsg(e)); });
  useEffect(() => {
    load();
    if (fields.some((f) => f.type === "doc") && can("documents", "r")) api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  }, [path]); // eslint-disable-line
  const save = async (data) => {
    try {
      if (edit.id) await api.put(`${path}/${edit.id}`, data); else await api.post(path, data);
      toast.success("Registro salvo"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`${path}/${del.id}`); toast.success("Registro removido"); setDel(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const rows = items ? (filter ? items.filter(filter) : items) : null;
  const newBtn = writable && <button className="btn-primary" data-testid={`${testid}-new-btn`} onClick={() => setEdit({ ...defaults })}><Plus className="h-4 w-4" />Novo</button>;
  return (
    <div data-testid={testid}>
      {headerless ? <div className="mb-4 flex justify-end">{newBtn}</div> : <PageHeader title={title} subtitle={subtitle}>{newBtn}</PageHeader>}
      {notice}
      {!rows ? <Loading /> : rows.length === 0 ? <Empty /> : (
        <>
          <div className="card-soft hidden overflow-hidden md:block">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F6F8] text-left text-xs uppercase tracking-wide text-[#587178]"><tr>{columns.map((c) => <th key={c.key} className={`px-4 py-3 font-semibold ${c.type === "money" ? "text-right" : ""}`}>{c.label}</th>)}<th className="w-24" /></tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={r.id} className="border-t border-[#DFE6E9] transition-colors hover:bg-[#F4F6F8]/60" data-testid={`${testid}-row`}>
                  {columns.map((c) => <td key={c.key} className={`px-4 py-3 ${c.type === "money" ? "text-right font-semibold" : ""}`}>{fmt(c.type, r[c.key], docs)}</td>)}
                  <td className="px-2 py-3"><div className="flex justify-end gap-1">{rowActions?.(r, load)}
                    {writable && <><button className="rounded-lg p-2 hover:bg-[#E3FAF7]" onClick={() => setEdit(r)} data-testid={`${testid}-edit-${r.id}`} aria-label="Editar"><Pencil className="h-4 w-4" /></button>
                      <button className="rounded-lg p-2 text-[#D32F2F] hover:bg-[#FDE8E8]" onClick={() => setDel(r)} data-testid={`${testid}-delete-${r.id}`} aria-label="Excluir"><Trash2 className="h-4 w-4" /></button></>}</div></td>
                </tr>))}</tbody>
            </table>
          </div>
          <div className="space-y-3 md:hidden">{rows.map((r) => (
            <div key={r.id} className="card-soft p-4" data-testid={`${testid}-card`}>
              <div className="flex items-start justify-between gap-3"><p className="font-bold text-[#12343B]">{fmt(columns[0].type, r[columns[0].key], docs)}</p>{columns.find((c) => c.type === "status") && fmt("status", r[columns.find((c) => c.type === "status").key])}</div>
              <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">{columns.slice(1).filter((c) => c.type !== "status").map((c) => <div key={c.key}><dt className="text-xs text-[#587178]">{c.label}</dt><dd className="font-medium">{fmt(c.type, r[c.key], docs)}</dd></div>)}</dl>
              <div className="mt-3 flex flex-wrap gap-2">{rowActions?.(r, load)}{writable && <><button className="btn-ghost h-10 flex-1" onClick={() => setEdit(r)}>Editar</button><button className="btn-ghost h-10 text-[#D32F2F]" onClick={() => setDel(r)}>Excluir</button></>}</div>
            </div>))}</div>
        </>)}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>{edit?.id ? "Editar" : "Novo"} — {title}</DialogTitle></DialogHeader>
          {edit && <CrudForm fields={fields} initial={edit} onSubmit={save} />}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Confirmar exclusão</AlertDialogTitle><AlertDialogDescription>O registro será removido das listas e a ação ficará registrada na auditoria.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete" onClick={remove} className="bg-[#D32F2F]">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
